import { getConfig } from '../../configs/config';
import { piiChecks } from '../../constants';
import messages from '../../constants/logMessages.json';
import logging, { getMessage } from '../logging';

const { errorHeading, errorBold } = messages;

/**
 * Logs a warning when a PII violation is detected.
 * @param {string} piiKey - The key identifying the type of PII.
 * @param {string} [dataPointKey] - The key of the data point containing the PII.
 * @param {string} [dataPointValue] - The value of the data point containing the PII.
 */
const piiWarningHandler = (
  piiKey: string,
  dataPointKey?: string,
  dataPointValue?: string
) => {
  const message = dataPointKey
    ? getMessage('piiViolation', [
        `${dataPointKey.toUpperCase()}:${dataPointValue}`,
        piiKey.toUpperCase(),
      ])
    : getMessage('adsPiiViolation', [piiKey.toUpperCase()]);

  logging.warn(message, errorHeading, errorBold);
};

/**
 * Validates a given attribute against a PII regex.
 * @param {RegExp} regex - The regular expression to test the attribute against.
 * @param {number} index - The index of the PII check.
 * @param {string} attribute - The attribute value to be tested.
 * @param {string} [dataPointKey] - The key of the data point.
 * @returns {boolean} - Returns true if a PII violation is found, otherwise false.
 */
const piiValidator = (
  regex: RegExp,
  index: number,
  attribute: string,
  dataPointKey?: string
): boolean => {
  // Special handling for credit card numbers
  if (/(master|visa|amex)Card/.test(Object.keys(piiChecks)[index])) {
    const creditCardAttribute = attribute.replace(/(\s+|-)/g, '');
    if (regex.test(creditCardAttribute)) {
      piiWarningHandler(Object.keys(piiChecks)[index], dataPointKey, attribute);
      return true;
    }
    return false;
  }

  // General PII validation
  if (regex.test(attribute) && attribute) {
    piiWarningHandler(Object.keys(piiChecks)[index], dataPointKey, attribute);
    return true;
  }
  return false;
};

/**
 * Checks if an attribute contains PII.
 * @param {string} attribute - The attribute to be checked.
 * @param {string} [dataPointKey] - The key of the data point.
 * @returns {boolean|string} - Returns 'mask' if PII is found and a data point key is provided, true if PII is found without a data point key, otherwise false.
 */
export const checkForPII = (
  attribute: string,
  dataPointKey: string = ''
): boolean | string => {
  if (!attribute) return false;

  const { bypassPiiCheckKeys } = getConfig();

  // Check if dataPointKey is in bypassPiiCheckKeys after splitting and trimming
  if (bypassPiiCheckKeys && dataPointKey) {
    const bypassPiiCheckKeysSplit = bypassPiiCheckKeys.flatMap((key) =>
      key.split(',').map((item) => item.trim())
    );

    const isBypassed = bypassPiiCheckKeysSplit.some(
      (key) => key === dataPointKey
    );
    if (isBypassed) return attribute;
  }

  const containsPII = Object.keys(piiChecks)
    .map((key) => piiChecks[key as keyof typeof piiChecks])
    .some((regex, index) =>
      piiValidator(regex, index, attribute.toString(), dataPointKey)
    );

  if (containsPII) return 'mask';
  return attribute;
};

/**
 * Checks a data object for PII in its values.
 * @param {object} dataObject - The object containing data points to be checked for PII.
 * @returns {object} - Returns a new object with PII values masked or unaltered.
 */
export const checkDataPointsForPII = (dataObject: object): object => {
  let contextData = {};
  Object.entries(dataObject).forEach(([key, value]) => {
    contextData = {
      ...contextData,
      [key]: checkForPII(value as string, key),
    };
  });
  return contextData;
};
